/**
 * Parses a text string containing link placeholders and bold tags, converting them into React elements.
 *
 * @param text - The input text string containing optional link placeholders in the format `<index>text</index>` and bold tags in the format `<b>text</b>`
 * @param links - An array of URLs corresponding to the link placeholders by index
 * @returns An array of React nodes containing text, anchor elements, and strong elements
 *
 * @example
 * ```typescript
 *
 * const result = parseText(
 *   'Click <0>here</0> for <b>important</b> info',
 *   ['https://example.com']
 * );
 * // Returns: [text node, <a>here</a>, text node, <strong>important</strong>, text node]
 *
 * const result2 = parseText(
 *   'No links here, just <b>bold</b> text',
 *   []
 * );
 * // Returns: [text node, <strong>bold</strong>, text node]
 *
 * @remarks
 * - Link placeholders use zero-based indexing to reference URLs in the links array
 * - URLs without a protocol are prefixed with 'https://'
 * - Links open in a new tab with security attributes (noopener, noreferrer)
 * - Invalid or missing link indices default to href="#"
 * - Empty input text returns an empty array
 * - Bold tags are converted to <strong> elements with a "font-bold" class
 */
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Optional per-link behaviour for placeholder (`<n>…</n>`) links. `onClick`
 * fires with the link's zero-based index and resolved href when clicked — used
 * to emit analytics (e.g. the online-registration `cta_click`) on inline links,
 * matching production which renders the online link inside the sentence. `id`
 * sets the anchor's DOM id (kept for existing selectors like `online-link-cta`).
 */
export type ParseTextLinkOptions = {
  onClick?: (index: number, href: string) => void;
  id?: string;
};

export const parseText = (
  text: string,
  links?: string[],
  linkOptions?: ParseTextLinkOptions,
): ReactNode[] => {
  if (!text) return [];

  const segments = text.split(/(<\d+>.*?<\/\d+>|<b>.*?<\/b>)/g);

  return segments.map((segment, i) => {
    const textToLink = segment.match(/^<(\d+)>(.*?)<\/\1>$/);
    if (textToLink && links) {
      const linkIndex = Number(textToLink[1]);
      const href = links[linkIndex];

      // Determine the full href:
      // - If href is missing/empty, default to '#'
      // - If href starts with a supported scheme, use it as-is
      // - If it's an internal app route ("/…"), use it as-is (SPA navigation)
      // - Otherwise, prepend https://
      let fullHref: string;
      const isInternalRoute = !!href && href.startsWith('/');
      const hasScheme = !!href && /^(https?:|mailto:|tel:|fax:)/.test(href);
      if (!href) {
        fullHref = '#';
      } else if (hasScheme || isInternalRoute) {
        fullHref = href;
      } else {
        fullHref = `https://${href}`;
      }

      const handleClick = linkOptions?.onClick
        ? () => linkOptions.onClick?.(linkIndex, fullHref)
        : undefined;

      // Internal app routes use react-router's <Link> for SPA navigation;
      // external/scheme links use a plain <a> (correct semantics, and no router
      // context needed — matches the previous hand-written online-link anchor).
      return isInternalRoute ? (
        <Link
          className="text-body-link-strong"
          key={i}
          to={fullHref}
          id={linkOptions?.id}
          onClick={handleClick}
        >
          {textToLink[2]}
        </Link>
      ) : (
        <a
          className="text-body-link-strong"
          key={i}
          href={fullHref}
          target="_blank"
          rel="noopener noreferrer"
          id={linkOptions?.id}
          onClick={handleClick}
        >
          {textToLink[2]}
        </a>
      );
    }
    const boldMatch = segment.match(/^<b>(.*?)<\/b>$/);
    if (boldMatch) {
      return (
        <strong key={i} className="font-bold">
          {boldMatch[1]}
        </strong>
      );
    }

    // Check is segment is ul or ol tag
    const listMatch = segment.match(/^<(ul|ol)>(.*?)<\/\1>$/s);
    if (listMatch) {
      const ListTag = listMatch[1] as 'ul' | 'ol';
      const listItems = listMatch[2].split(/<li>(.*?)<\/li>/g).filter((item) => item.trim() !== '');
      return (
        <ListTag key={i} className="list-disc pl-5 mb-space-300">
          {listItems.map((item, index) => (
            <li key={index} className="p-1">
              {item}
            </li>
          ))}
        </ListTag>
      );
    }

    // return plain text if no match
    return segment;
  });
};
